import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from 'reactstrap';
import { FRONTEND_FEATURES } from './AdminDashboard';
import { EMR_OPTIONS } from '../constants/EmrNoteTypeMapping';

const EMPTY_FORM = {
  flag: '',
  value: 'true',
  pctPerDay: 10,
  maxPct: 100,
  includeEmrs: [],
  excludeEmrs: [],
  excludeGroupIds: '',
  excludeEmails: '',
};

const STATUS_COLORS = {
  draft: 'secondary',
  active: 'success',
  paused: 'warning',
  complete: 'primary',
  rolled_back: 'danger',
};

const splitList = (text) =>
  text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const parseValue = (v) => (v === 'true' ? true : v === 'false' ? false : v);

// Gradual rollouts (COM-474): the daily CRON turns a flag on for pct_per_day of
// users per day, up to max_pct.
const FeatureRollouts = ({ apiClient }) => {
  const [rollouts, setRollouts] = useState([]);
  const [features, setFeatures] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState(null);
  const [users, setUsers] = useState({ flag: null, list: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const rsp = await apiClient.adminListRollouts();
    setRollouts(Array.isArray(rsp?.rollouts) ? rsp.rollouts : []);
  }, [apiClient]);

  useEffect(() => {
    load().catch(() =>
      setStatus({ error: true, text: 'Unable to load rollouts.' }),
    );
    apiClient
      .adminGetBackendFeatures()
      .then((rsp) =>
        setFeatures({ ...(rsp?.features || rsp || {}), ...FRONTEND_FEATURES }),
      )
      .catch(() => setFeatures({ ...FRONTEND_FEATURES }));
  }, [apiClient, load]);

  const run = async (fn, successText) => {
    setBusy(true);
    setStatus(null);
    try {
      const rsp = await fn();
      if (rsp?.error) {
        setStatus({ error: true, text: rsp.error });
      } else {
        setStatus({ error: false, text: successText(rsp) });
        await load();
      }
    } catch (err) {
      setStatus({ error: true, text: String(err) });
    } finally {
      setBusy(false);
    }
  };

  const emrSelect = (key) => (
    <Input
      type="select"
      multiple
      value={form[key]}
      onChange={(e) =>
        setForm({
          ...form,
          [key]: [...e.target.selectedOptions].map((o) => o.value),
        })
      }
    >
      {EMR_OPTIONS.map((emr) => (
        <option key={emr} value={emr}>
          {emr}
        </option>
      ))}
    </Input>
  );

  const valueOptions = (features[form.flag] || [true, false]).map(String);

  const save = () =>
    run(
      () =>
        apiClient.adminSaveRollout({
          flag: form.flag,
          value: parseValue(form.value),
          pctPerDay: Number(form.pctPerDay),
          maxPct: Number(form.maxPct),
          includeEmrs: form.includeEmrs,
          excludeEmrs: form.excludeEmrs,
          excludeGroupIds: splitList(form.excludeGroupIds),
          excludeEmails: splitList(form.excludeEmails),
        }),
      () => `Saved rollout for ${form.flag}.`,
    );

  const edit = (r) =>
    setForm({
      flag: r.flag,
      value: String(r.value),
      pctPerDay: r.pct_per_day,
      maxPct: r.max_pct,
      includeEmrs: r.include_emrs || [],
      excludeEmrs: r.exclude_emrs || [],
      excludeGroupIds: (r.exclude_group_ids || []).join(', '),
      excludeEmails: (r.exclude_emails || []).join(', '),
    });

  const setRolloutStatus = (flag, action) =>
    run(
      () => apiClient.adminSetRolloutStatus(flag, action),
      () => `${flag}: ${action}ed.`,
    );

  const rollback = (flag) => {
    if (
      !window.confirm(`Turn ${flag} off for every user this rollout turned on?`)
    )
      return;
    run(
      () => apiClient.adminRollbackRollout(flag),
      (rsp) => `${flag}: reverted ${rsp.reverted} user(s).`,
    );
  };

  const showUsers = async (flag) => {
    const rsp = await apiClient.adminGetRolloutUsers(flag);
    setUsers({ flag, list: Array.isArray(rsp?.users) ? rsp.users : [] });
  };

  return (
    <div>
      <Alert color="warning">
        Rollouts only cover existing users. To turn a flag on for new users,
        ask the dev team.
      </Alert>
      <h5>Rollout</h5>
      <Row>
        <Col md={3}>
          <FormGroup>
            <Label>Flag</Label>
            <Input
              type="select"
              value={form.flag}
              onChange={(e) =>
                setForm({
                  ...form,
                  flag: e.target.value,
                  value: String((features[e.target.value] || [true])[0]),
                })
              }
            >
              <option value="">Select flag</option>
              {Object.keys(features)
                .sort()
                .map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup>
            <Label>Value</Label>
            <Input
              type="select"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            >
              {valueOptions.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup>
            <Label>% of users per day</Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={form.pctPerDay}
              onChange={(e) => setForm({ ...form, pctPerDay: e.target.value })}
            />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup>
            <Label>Max %</Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={form.maxPct}
              onChange={(e) => setForm({ ...form, maxPct: e.target.value })}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>
              Include EMRs (none = all users; % is of these EMRs&apos; users)
            </Label>
            {emrSelect('includeEmrs')}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>Exclude EMRs</Label>
            {emrSelect('excludeEmrs')}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>Exclude group subscription IDs (comma separated)</Label>
            <Input
              type="textarea"
              rows={2}
              value={form.excludeGroupIds}
              onChange={(e) =>
                setForm({ ...form, excludeGroupIds: e.target.value })
              }
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>Exclude emails (comma separated)</Label>
            <Input
              type="textarea"
              rows={2}
              value={form.excludeEmails}
              onChange={(e) =>
                setForm({ ...form, excludeEmails: e.target.value })
              }
            />
          </FormGroup>
        </Col>
      </Row>
      <Button color="primary" disabled={busy || !form.flag} onClick={save}>
        Save
      </Button>
      <Button
        color="secondary"
        disabled={busy}
        onClick={() => setForm(EMPTY_FORM)}
      >
        Clear
      </Button>
      {status && (
        <p
          style={{ marginTop: 12, color: status.error ? '#f5365c' : '#2dce89' }}
        >
          {status.text}
        </p>
      )}

      <h5 style={{ marginTop: 24 }}>Rollouts</h5>
      <Table responsive size="sm">
        <thead>
          <tr>
            <th>Flag</th>
            <th>Value</th>
            <th>Status</th>
            <th>Speed</th>
            <th>Target</th>
            <th>Last run</th>
            <th>Included EMRs</th>
            <th>Excluded</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rollouts.map((r) => {
            const last = r.last_run || {};
            return (
              <tr key={r.flag}>
                <td>{r.flag}</td>
                <td>{String(r.value)}</td>
                <td>
                  <Badge color={STATUS_COLORS[r.status] || 'secondary'}>
                    {r.status}
                  </Badge>
                </td>
                <td>
                  {r.pct_per_day}%/day, max {r.max_pct}%
                </td>
                <td>
                  {r.target_pct}% (day {r.active_days || 0})
                </td>
                <td>
                  {r.last_run_at
                    ? `${new Date(r.last_run_at + 'Z').toLocaleString()}: ${last.on_count}/${last.total_users} on (+${last.enabled}, -${last.reverted})`
                    : '—'}
                </td>
                <td>{(r.include_emrs || []).join(', ') || 'All'}</td>
                <td>
                  {(r.exclude_emrs || []).length} EMRs,{' '}
                  {(r.exclude_group_ids || []).length} groups,{' '}
                  {(r.exclude_emails || []).length} emails
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Button size="sm" onClick={() => edit(r)}>
                    Edit
                  </Button>
                  {r.status === 'draft' && (
                    <Button
                      size="sm"
                      color="success"
                      disabled={busy}
                      onClick={() => setRolloutStatus(r.flag, 'start')}
                    >
                      Start
                    </Button>
                  )}
                  {r.status === 'active' && (
                    <Button
                      size="sm"
                      color="warning"
                      disabled={busy}
                      onClick={() => setRolloutStatus(r.flag, 'pause')}
                    >
                      Pause
                    </Button>
                  )}
                  {r.status === 'paused' && (
                    <Button
                      size="sm"
                      color="success"
                      disabled={busy}
                      onClick={() => setRolloutStatus(r.flag, 'resume')}
                    >
                      Resume
                    </Button>
                  )}
                  {r.status !== 'draft' && r.status !== 'rolled_back' && (
                    <Button
                      size="sm"
                      color="danger"
                      disabled={busy}
                      onClick={() => rollback(r.flag)}
                    >
                      Roll back
                    </Button>
                  )}
                  <Button
                    size="sm"
                    color="info"
                    onClick={() => showUsers(r.flag)}
                  >
                    Users
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {users.flag && (
        <>
          <h5 style={{ marginTop: 24 }}>
            {users.flag}: {users.list.length} user(s) turned on by rollout
          </h5>
          <Table responsive size="sm">
            <thead>
              <tr>
                <th>Email</th>
                <th>Group</th>
                <th>Group ID</th>
                <th>Turned on</th>
                <th>Current value</th>
              </tr>
            </thead>
            <tbody>
              {users.list.map((u) => (
                <tr key={u.email}>
                  <td>{u.email}</td>
                  <td>{u.group}</td>
                  <td>{u.group_id}</td>
                  <td>{u.enabled_on}</td>
                  <td>{String(u.value)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
};

export default FeatureRollouts;
